'use client';

import { ReactNode } from "react";
import { Box } from "@mui/material";
import clsx from "clsx";
import { signatureBorderStyle } from "@/app/theme/theme-tokens";
import SplitContentProvider, { useSplitContent } from "../contexts/SplitContentProvider";
import DragHandle from "./DragHandle";
import styles from "./split-content.module.css";

interface SplitContentProps {
    content: ReactNode;
    background: ReactNode;
    backgroundMode?: BackgroundMode;
    variant?: Variant;
    initialSplitPosition?: number;
    maxSplitPosition?: number;
    baseHeight?: string;
    resizingDisabled?: boolean;
    alignment?: Orientation;
}

export type SplitContentOrientation = Orientation;

function SplitContentLayout({
    content,
    background,
    variant = 'bordered',
    baseHeight = '100vh',
}: Pick<SplitContentProps, 'content' | 'background' | 'variant' | 'baseHeight'>) {
    const {
        orientation,
        splitPosition,
        maxSplitPosition,
        dragContainerRef,
        isSnapAnimate,
        resizingDisabled,
        handlePointerDown,
        handleDragHandleClick,
        backgroundMode
    } = useSplitContent();

    return (
        <Box
            className={clsx(
                styles.layoutContainer,
                variant === 'bordered' && 'p-4'
            )}
            sx={{
                "--base-height": baseHeight,
            }}
        >
            <Box
                ref={dragContainerRef}
                className={clsx("relative flex flex-auto overflow-hidden", styles.dragContainer)}
                sx={{
                    flexDirection: orientation,
                    ...(variant === 'bordered' && {
                        border: (theme) => `1px solid ${theme.palette.primary.main}`,
                        ...signatureBorderStyle
                    })
                }}
            >
                <Foreground
                    orientation={orientation}
                    splitPosition={splitPosition}
                    animate={isSnapAnimate}
                >
                    {content}
                </Foreground>
                <Background
                    backgroundMode={backgroundMode}
                    orientation={orientation}
                    splitPosition={splitPosition}
                    animate={isSnapAnimate}
                >
                    {background}
                </Background>
                {!resizingDisabled && (
                    <DragHandle
                        orientation={orientation}
                        splitPosition={splitPosition}
                        maxSplitPosition={maxSplitPosition}
                        onPointerDown={handlePointerDown}
                        onClick={handleDragHandleClick}
                        animate={isSnapAnimate}
                    />
                )}
            </Box>
        </Box>
    );
}

function getForegroundClassName(orientation: Orientation): string {
    switch (orientation) {
        case 'row':
            return styles.splitContentContentRow;
        case 'row-reverse':
            return styles.splitContentContentRowReverse;
        case 'column':
            return styles.splitContentContentColumn;
        case 'column-reverse':
            return styles.splitContentContentColumnReverse;
    }
}

function getBackgroundClassName(orientation: Orientation): string {
    switch (orientation) {
        case 'row':
            return styles.splitContentBackgroundRow;
        case 'row-reverse':
            return styles.splitContentBackgroundRowReverse;
        case 'column':
            return styles.splitContentBackgroundColumn;
        case 'column-reverse':
            return styles.splitContentBackgroundColumnReverse;
    }
}

function Foreground({
    children,
    splitPosition,
    orientation,
    animate,
}: {
    children: ReactNode,
    splitPosition: number,
    orientation: Orientation
    animate?: boolean,
}) {
    const orientationAwareClassName = getForegroundClassName(orientation);

    return (
        <Box
            className={clsx(
                styles.splitContentContent,
                orientationAwareClassName,
                animate && styles.splitContentContentAnimated
            )}
            sx={{
                "--split-position": `${splitPosition}%`,
            }}
        >
            <Box className={clsx("flex flex-auto min-h-0 min-w-0 md:max-h-full overflow-hidden p-4 md:p-24", styles.foregroundInner)}>
                {children}
            </Box>
        </Box>
    )
}

function Background({
    children,
    splitPosition,
    orientation,
    backgroundMode,
    animate,
}: {
    children: ReactNode,
    splitPosition: number,
    orientation: Orientation,
    backgroundMode: BackgroundMode,
    animate?: boolean,
}) {
    const orientationAwareClassName = getBackgroundClassName(orientation);

    return (
        <Box
            className={clsx(
                styles.splitContentBackground,
                orientationAwareClassName,
                animate && styles.splitContentBackgroundAnimated,
                backgroundMode === 'fill'
                    ? styles.splitContentBackgroundFull
                    : styles.splitContentBackgroundAdaptive,
            )}
            sx={{
                "--split-position": `${splitPosition}%`,
            }}
        >
            {children}
        </Box>
    )
}

export default function SplitContent({
    content,
    background,
    initialSplitPosition = 50,
    maxSplitPosition = initialSplitPosition,
    baseHeight = '100vh',
    backgroundMode = 'fill',
    variant = 'bordered',
    resizingDisabled = false,
    alignment = 'row',
}: SplitContentProps) {
    return (
        <SplitContentProvider
            initialSplitPosition={initialSplitPosition}
            maxSplitPosition={maxSplitPosition}
            resizingDisabled={resizingDisabled}
            orientation={alignment}
            backgroundMode={backgroundMode}
        >
            <SplitContentLayout
                content={content}
                background={background}
                variant={variant}
                baseHeight={baseHeight}
            />
        </SplitContentProvider>
    );
}
